import React, { useCallback, isValidElement } from 'react';
import { styled } from '@compiled/react';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import { colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
import { useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import {
	type MenuItemNode,
	type MenuItemNodeArgs,
	type MenuNode,
	MenuNodeType,
} from '../../../../common/types';
import { ContextMenuNodeList } from '../../../../common/ui/context-menu-node-list';
import {
	useContextMenuAnalytics,
	ContextualAnalyticsDataFromNode,
} from '../../../../controllers/analytics/main';
import { useContextMenuActions } from '../../../../controllers/context-menu';
import { BulkIndicator } from './bulk-indicator';
import { CONTEXT_MENU_SEPARATOR_TESTID, CONTEXT_MENU_BULK_INDICATOR_TESTID } from './constants';
import { ContextMenuItem } from './context-menu-item';
import { ContextMenuSearch } from './context-menu-search';
import { ContextMenuSection } from './context-menu-section';

export type ContextMenuNodeProps = {
	scope: string;
	menuNode: MenuNode;
	onClick: (node: MenuNode, analyticsEvent: UIAnalyticsEvent) => void;
	onMouseEnter: (node: MenuNode) => void;
	shouldRenderToParent?: boolean;
};

export const ContextMenuNode = ({
	scope,
	menuNode,
	onClick,
	onMouseEnter,
	shouldRenderToParent = true,
}: ContextMenuNodeProps) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { addMappedAnalyticsToEvent, fireUIAnalyticsForNode } = useContextMenuAnalytics();
	const { closeMenu } = useContextMenuActions();

	const getMenuItemNodeProps = useCallback(
		(menuItem: MenuItemNode): MenuItemNodeArgs => {
			const analyticsEvent = addMappedAnalyticsToEvent(
				createAnalyticsEvent({
					actionSubject: 'dropdownItem',
					action: 'clicked',
				}),
				menuItem.id,
				menuItem.analytics,
			);

			return {
				scope,
				// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
				analyticsEvent: analyticsEvent.clone() as UIAnalyticsEvent,
				closeMenu: () => {
					closeMenu(scope);
					fireUIAnalyticsForNode(analyticsEvent, menuItem.id, menuItem.analytics);
				},
				onClick,
				onMouseEnter,
			};
		},
		[
			createAnalyticsEvent,
			fireUIAnalyticsForNode,
			addMappedAnalyticsToEvent,
			closeMenu,
			scope,
			onClick,
			onMouseEnter,
		],
	);

	const renderNode = useCallback(
		(node: MenuNode) => (
			<ContextMenuNode
				scope={scope}
				menuNode={node}
				onClick={onClick}
				onMouseEnter={onMouseEnter}
				shouldRenderToParent={shouldRenderToParent}
			/>
		),
		[onClick, onMouseEnter, scope, shouldRenderToParent],
	);

	if (menuNode.type === MenuNodeType.Section) {
		return (
			<ContextMenuSection section={menuNode}>
				{menuNode.children && (
					<ContextMenuNodeList menuNodes={menuNode.children} renderNode={renderNode} />
				)}
			</ContextMenuSection>
		);
	}
	if (menuNode.type === MenuNodeType.Item) {
		return (
			<ContextualAnalyticsDataFromNode id={menuNode.id} updater={menuNode.analytics}>
				<ContextMenuItem
					scope={scope}
					menuItem={menuNode}
					onClick={onClick}
					onMouseEnter={onMouseEnter}
					shouldRenderToParent={shouldRenderToParent}
				>
					{isValidElement(menuNode.element)
						? menuNode.element
						: menuNode.children && (
								<ContextMenuNodeList menuNodes={menuNode.children} renderNode={renderNode} />
							)}
				</ContextMenuItem>
			</ContextualAnalyticsDataFromNode>
		);
	}
	if (menuNode.type === MenuNodeType.Node) {
		return (
			<ContextualAnalyticsDataFromNode id={menuNode.id} updater={menuNode.analytics}>
				{typeof menuNode.node === 'function'
					? menuNode.node(getMenuItemNodeProps(menuNode))
					: menuNode.node}
			</ContextualAnalyticsDataFromNode>
		);
	}
	if (menuNode.type === MenuNodeType.Separator) {
		return <ContextMenuSeparator tabIndex={-1} data-testid={CONTEXT_MENU_SEPARATOR_TESTID} />;
	}

	if (menuNode.type === MenuNodeType.BulkIndicator) {
		return (
			<BulkIndicator
				selectedCount={menuNode.selectedCount}
				data-testid={CONTEXT_MENU_BULK_INDICATOR_TESTID}
			/>
		);
	}

	if (menuNode.type === MenuNodeType.Search) {
		return (
			<ContextMenuSearch
				onChange={menuNode.onChange}
				autoFocus={menuNode.autoFocus}
				maxViewportItems={menuNode.maxViewportItems}
				hasSearch={menuNode.hasSearch}
				isAsyncSearch={menuNode.isAsyncSearch}
				isLoading={menuNode.isLoading ?? false}
				hasResult={!!menuNode.children?.length}
				ariaLabel={menuNode.ariaLabel}
				resultsCount={menuNode.children.length}
			>
				{!menuNode.isLoading && !!menuNode.children?.length && (
					<ContextMenuNodeList menuNodes={menuNode.children} renderNode={renderNode} />
				)}
			</ContextMenuSearch>
		);
	}

	return null;
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ContextMenuSeparator = styled.div({
	height: 1,
	width: '100%',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	background: token('color.border', colors.N30),
	margin: `${token('space.050', '4px')} 0px`,
});
